import { Link, useNavigate } from "react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormCreator, FormCreatorProps, FormFieldDef } from "@/components/shared/form-creator";
import { useAppDispatch, useAppSelector } from "@/lib/store";
import { updatePassword } from "@/app/auth/reset-password/reset-password.slice";
import { useEffect } from "react";
import supabase from "@/lib/supabase.ts";

const formSchema = z
  .object({
    password: z.string({ required_error: "Password is required" }).min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string({ required_error: "Please confirm your password" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

export function ResetPasswordForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, success, message } = useAppSelector((state) => state.resetPassword);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Check if we have a valid session from the reset link
  useEffect(() => {
    const checkSession = async () => {
      // Supabase automatically processes hash fragments containing auth tokens
      // Check if we have hash fragments in the URL (from the email link)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get("access_token");
      const type = hashParams.get("type");

      // If we have an access token in the hash, Supabase should have processed it
      // Wait a moment for Supabase to process it, then check the session
      if (accessToken && type === "recovery") {
        // Give Supabase a moment to process the hash
        setTimeout(async () => {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (!session || error) {
            // If no session after processing hash, redirect to forgot password page
            navigate("/forgot-password");
          }
        }, 100);
      } else {
        // Check existing session
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!session || error) {
          // If no session, redirect to forgot password page
          navigate("/forgot-password");
        }
      }
    };
    checkSession();
  }, [navigate]);

  async function onSubmit(values: { password: string; confirmPassword: string }) {
    await dispatch(updatePassword(values)).unwrap();
    // After successful password reset, redirect to login after a short delay
    setTimeout(() => {
      navigate("/login");
    }, 2000);
  }

  const formDefinition: FormFieldDef[] = [
    {
      type: "password",
      name: "password",
      label: "New Password",
      placeholder: "Enter your new password",
      required: true,
    },
    {
      type: "password",
      name: "confirmPassword",
      label: "Confirm Password",
      placeholder: "Confirm your new password",
      required: true,
    },
  ];

  const formCreatorProps: FormCreatorProps = {
    form,
    onSubmit,
    formDefinition,
    buttonText: loading ? "Resetting..." : "Reset Password",
  };

  if (success) {
    return (
      <>
        <div className="flex flex-col items-start gap-2 text-center mb-6">
          <h1 className="text-2xl font-bold">Password reset successful!</h1>
          <p className="text-balance text-sm text-muted-foreground">
            {message || "Your password has been reset successfully. Redirecting to login..."}
          </p>
        </div>
        <div className="text-center text-sm mt-4">
          <Link to="/login" className="underline underline-offset-4">
            Go to login
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Reset your password</h1>
        <p className="text-balance text-sm text-muted-foreground">
          Enter your new password below
        </p>
      </div>

      <FormCreator {...formCreatorProps} />

      {/* TODO: Error formatting. SYN-61 */}
      {error && <p className="text-sm text-destructive text-center mt-4">{error}</p>}

      <div className="text-center text-sm mt-4">
        <Link to="/login" className="underline underline-offset-4">
          Back to login
        </Link>
      </div>
    </>
  );
}

